import Spinner from '@legacy/core/components/Spinner';
import { Component } from "react";
import deepcopy from "rfdc";
import ContactForm from "../clients/forms/ContactForm";
import { PriceBookItemTypes } from '../core/utils/enums';
import { historyHasState, sendDataToServer, validateSendContact, valueIsDefined } from "../core/utils/utils";
import CreditNoteDetailsCard from "./components/CreditNoteDetailsCard";
import CreditNoteSendForm from "./forms/CreditNoteSendForm";
import CreditNoteVoidForm from "./forms/CreditNoteVoidForm";

const TAX_POLLING_INTERVAL_MS = 5000

const PAGE_MODES = {
    VIEW_CREDIT_NOTE: "VIEW_CREDIT_NOTE",
    SEND_CREDIT_NOTE: "SEND_CREDIT_NOTE",
    VOID_CREDIT_NOTE: "VOID_CREDIT_NOTE",
    ADD_CONTACT: "ADD_CONTACT",
    EDIT_CONTACT: "EDIT_CONTACT",
}

const PAGE_MODE_SUBTITLES = {
    VIEW_CREDIT_NOTE: "Credit Note Details",
    SEND_CREDIT_NOTE: "Send Credit Note",
    VOID_CREDIT_NOTE: "Void Credit Note",
    ADD_CONTACT: "Add Contact",
    EDIT_CONTACT: "Edit Contact",
}

const PAGE_MODE_BACK_BUTTON_DISPLAY = {
    VIEW_CREDIT_NOTE: "flex",
    SEND_CREDIT_NOTE: "none",
    VOID_CREDIT_NOTE: "none",
    ADD_CONTACT: "none",
    EDIT_CONTACT: "none",
}

const PRIMARY_PAGE_MODES = [PAGE_MODES.VIEW_CREDIT_NOTE]
const SECONDARY_PAGE_MODES = [
    PAGE_MODES.SEND_CREDIT_NOTE,
    PAGE_MODES.VOID_CREDIT_NOTE,
    PAGE_MODES.ADD_CONTACT,
    PAGE_MODES.EDIT_CONTACT,
]

const CONTACT_PAGE_MODES = [
    PAGE_MODES.ADD_CONTACT,
    PAGE_MODES.EDIT_CONTACT,
]

const FORM_DATA_NAMES_BY_MODE = {
    VIEW_CREDIT_NOTE: "creditNoteData",
    SEND_CREDIT_NOTE: "sendData",
    VOID_CREDIT_NOTE: "voidData",
    ADD_CONTACT: "contactData",
    EDIT_CONTACT: "contactData",
}

const SUBMITTING_NAMES_BY_MODE = {
    VIEW_CREDIT_NOTE: "submittingCreditNote",
    SEND_CREDIT_NOTE: "submittingSend",
    VOID_CREDIT_NOTE: "submittingVoid",
    ADD_CONTACT: "submittingContact",
    EDIT_CONTACT: "submittingContact",
}

const ERROR_NAMES_BY_MODE = {
    VIEW_CREDIT_NOTE: "creditNote",
    SEND_CREDIT_NOTE: "send",
    VOID_CREDIT_NOTE: "void",
    ADD_CONTACT: "contact",
    EDIT_CONTACT: "contact",
}


class CreditNoteDetailsContainer extends Component {

    // Initialize

    constructor(props) {
        super(props)

        const defaultMode = this.props.formMode || PAGE_MODES.VIEW_CREDIT_NOTE

        this.defaultClientMessage = window.CURRENT_USER?.service_company?.credit_note_default_client_message
        this.addToastToQueue = this.props.addToastToQueue

        this.state = {
            creditNoteData: null,

            sendData: {},
            voidData: {},
            contactData: {},

            selectedSendPhoneContacts: [],
            selectedSendEmailContacts: [],

            errors: {
                creditNote: {},
                send: {},
                void: {},
                contact: {},
            },

            defaultMode: defaultMode,
            mode: defaultMode,

            sendViaSMS: false,
            sendViaEmail: false,
            sendManually: false,

            inventoryReturned: false,

            taxPollingId: null,

            returnScroll: 0,
        }

        window.onpopstate = (event) => {
            if (event.state !== null && Object.keys(event.state).length) {
                this.setState(event.state)
            }
        }
    }

    componentDidUpdate() {
        // Mode switches and the data load mutate state in place, so prevState can't show them; this check is idempotent
        this.updateTaxPolling()
    }

    componentWillUnmount() {
        this.stopTaxPolling()
    }

    updateTaxPolling = () => {
        const { creditNoteData, taxPollingId, mode } = this.state

        if (!creditNoteData) {
            return
        }

        // Only poll when in send mode and tax isn't ready
        const shouldPoll = mode === PAGE_MODES.SEND_CREDIT_NOTE && creditNoteData.awaiting_automated_tax
        if (shouldPoll && !taxPollingId) {
            const intervalId = setInterval(this.pollTaxReadyStatus, TAX_POLLING_INTERVAL_MS)
            this.setState({ taxPollingId: intervalId })
        } else if (!shouldPoll && taxPollingId) {
            this.stopTaxPolling()
        }
    }

    stopTaxPolling = () => {
        if (this.state.taxPollingId) {
            clearInterval(this.state.taxPollingId)
            this.setState({ taxPollingId: null })
        }
    }

    pollTaxReadyStatus = async () => {
        try {
            const creditNoteEndpoint = DjangoUrls["invoices:api-credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, window.CREDIT_NOTE_ID)
            const response = await fetch(creditNoteEndpoint, { cache: "no-store" })
            if (!response.ok) return
            const updatedCreditNote = await response.json()

            // Take the tax the accounting system returned and the amounts it changed, along with the hold state
            if (updatedCreditNote.awaiting_automated_tax !== this.state.creditNoteData.awaiting_automated_tax) {
                this.setState((state) => ({
                    creditNoteData: {
                        ...state.creditNoteData,
                        awaiting_automated_tax: updatedCreditNote.awaiting_automated_tax,
                        tax_name: updatedCreditNote.tax_name,
                        tax_percent: updatedCreditNote.tax_percent,
                        tax_breakdown: updatedCreditNote.tax_breakdown,
                        tax_amount: updatedCreditNote.tax_amount,
                        total: updatedCreditNote.total,
                    },
                }))
            }
        } catch (_error) {
            // Ignore polling errors
        }
    }

    componentDidMount = async () => {
        if (this.state.creditNoteData === null) {
            let creditNoteEndpoint

            if (window.USING_PUBLIC_URL === true) {
                creditNoteEndpoint = window.PUBLIC_REST_URL
            }
            else {
                creditNoteEndpoint = DjangoUrls["invoices:api-credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, window.CREDIT_NOTE_ID)
            }

            const creditNoteResponse = await fetch(creditNoteEndpoint)
            const creditNote = await creditNoteResponse.json()

            this.setState((state, props) => {
                let updatedState = state
                updatedState.creditNoteData = creditNote

                // Split line items into their respective lists
                updatedState.creditNoteData.service_charges = creditNote.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.service)
                updatedState.creditNoteData.parts = creditNote.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.part)
                updatedState.creditNoteData.other_charges = creditNote.line_items.filter(lineItem => lineItem.line_item_type === PriceBookItemTypes.other)

                updatedState.inventoryReturned = creditNote.line_items.some(lineItem => lineItem.return_to_inventory === true)

                return updatedState
            })
        }

        if (historyHasState(history)) {
            if (document.querySelector(".page-subtitle")) {
                document.querySelector(".page-subtitle").innerHTML = PAGE_MODE_SUBTITLES[history.state.mode]
            }
            if (document.querySelector(".back-button")) {
                document.querySelector(".back-button").style.display = PAGE_MODE_BACK_BUTTON_DISPLAY[history.state.mode]
            }
            this.setState(history.state)
        }
        else {
            // If the URL asks for the send credit note page directly, oblige
            const desiredMode = new URLSearchParams(document.location.search).get("mode") || ""
            if (desiredMode === "send-credit-note") {
                this.handleActionRequest("CREDIT_NOTE_SEND")
            }
        }
    }

    // Form helpers

    updateFormData = (formName, fieldName, fieldValue, then=null) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        }, then)
    }

    switchFormMode = (mode) => {
        if (document.querySelector(".page-subtitle")) {
            document.querySelector(".page-subtitle").innerHTML = PAGE_MODE_SUBTITLES[mode]
        }
        if (document.querySelector(".back-button")) {
            document.querySelector(".back-button").style.display = PAGE_MODE_BACK_BUTTON_DISPLAY[mode]
        }

        if (SECONDARY_PAGE_MODES.includes(mode)) {
            history.replaceState(this.state, "", "")
        }

        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            history.pushState(updatedState, "", "?mode=" + mode.toLowerCase().replace(/_/g, "-"));
            return updatedState
        })
    }

    switchToPrimaryForm = () => {
        this.setState((state, props) => {
            let updatedState = state

            // Clear the secondary form data
            updatedState[FORM_DATA_NAMES_BY_MODE[state.mode]] = {}
            updatedState[SUBMITTING_NAMES_BY_MODE[state.mode]] = false
            updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = {}

            return updatedState
        })
        this.switchFormMode(this.state.defaultMode)
    }

    switchToSecondaryForm = (newFormMode, data, initialData) => {
        this.setState((state, props) => {
            let updatedState = state
            // Set the scroll state
            updatedState.returnScroll = document.querySelector(".main").scrollTop

            updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = {}

            if (data !== null) {
                updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]] = deepcopy()(data)
            }
            else {
                if (newFormMode === PAGE_MODES.ADD_CONTACT) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].attached_to = "external_client"
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].is_ephemeral = false
                }
                else if (newFormMode === PAGE_MODES.SEND_CREDIT_NOTE && this.defaultClientMessage) {
                    updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]].client_message = this.defaultClientMessage
                }
            }

            if (initialData !== null) {
                Object.assign(updatedState[FORM_DATA_NAMES_BY_MODE[newFormMode]], initialData)
            }

            return updatedState
        })

        this.switchFormMode(newFormMode)
    }

    updateSendPhoneSelection = (selectedContacts) => {
        this.setState((state, props) => {
            let updatedState = state

            updatedState.selectedSendPhoneContacts = selectedContacts
            updatedState.sendData.phone_contacts = selectedContacts

            return updatedState
        })
    }

    updateSendEmailSelection = (selectedContacts) => {
        this.setState((state, props) => {
            let updatedState = state

            updatedState.selectedSendEmailContacts = selectedContacts
            updatedState.sendData.email_contacts = selectedContacts

            return updatedState
        })
    }

    // Create credit note (finalize from draft)

    createCreditNote = async () => {
        const dataName = "creditNoteData"
        const submittingName = "submittingCreditNote"
        const errorDictName = "creditNote"
        const endpoint = DjangoUrls["invoices:api-credit-notes-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const dataManipulator = (data, state) => {
            let preparedData = deepcopy()(data)
            preparedData.is_draft = false

            // Convert invoice to ID
            if (data.invoice !== null) {
                preparedData.invoice = data.invoice.id
            }

            // Convert service company to slug
            preparedData.service_company = data.service_company.slug

            // Convert external client to ID
            preparedData.external_client = data.external_client.id

            return preparedData
        }

        const onSuccess = (creditNote) => {
            const successUrl = DjangoUrls["invoices:credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, creditNote.id)
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Credit Note "${creditNote.custom_id || creditNote.id}" created`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })

            history.replaceState({}, "", "")
            location.assign(successUrl)
        }
        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Credit Note could not be created",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, undefined)
    }

    // Send data

    submitSendData = async () => {
        const endpoint = DjangoUrls["invoices:api-credit-notes-send"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)
        const endpointMethod = "POST"
        const successUrl = DjangoUrls["invoices:credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)

        const dataName = "sendData"
        const submittingName = "submittingSend"
        const errorDictName = "send"

        const onSuccess = (json) => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Credit Note "${this.state.creditNoteData.custom_id || this.state.creditNoteData.id}" sent`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        const dataManipulator = (data, state) => {
            data.send_via_sms = state.sendViaSMS
            data.send_via_email = state.sendViaEmail
            data.send_manually = state.sendManually
            data.phone_contacts = data.phone_contacts || []
            data.email_contacts = data.email_contacts || []

            return data
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Credit Note could not be sent",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, undefined)
    }

    markCreditNoteAsSent = async () => {
        const endpoint = DjangoUrls["invoices:api-credit-notes-mark-as-sent"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)
        const endpointMethod = "POST"
        const successUrl = DjangoUrls["invoices:credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)

        const dataName = "sendData"
        const submittingName = "submittingSend"
        const errorDictName = "send"

        const onSuccess = () => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Credit Note "${this.state.creditNoteData.custom_id || this.state.creditNoteData.id}" marked as sent`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        const dataManipulator = (data, state) => {
            data.phone_contacts = data.phone_contacts || []
            data.email_contacts = data.email_contacts || []
            return data
        }

        document.querySelectorAll("#message_modal_mark_as_sent .modal__close .button").forEach(button => button.style.display = "none")
        document.querySelector("#message_modal_mark_as_sent .modal__close .spinner-centered").style.display = "block"

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Credit Note could not be marked as sent",
            })
            const markAsSentModal = document.querySelector("#message_modal_mark_as_sent .modal__close")
            if (markAsSentModal) {
                markAsSentModal.innerHTML = '<span class="text-invalid"><strong>An unexpected error occurred.</strong></span>'
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, dataManipulator, undefined)
    }

    // Void credit note

    voidCreditNote = async () => {
        const endpoint = DjangoUrls["invoices:api-credit-notes-void"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)
        const endpointMethod = "POST"
        const successUrl = DjangoUrls["invoices:credit-notes-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id)

        const dataName = "voidData"
        const submittingName = "submittingVoid"
        const errorDictName = "void"

        const onSuccess = (json) => {
            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: `Credit Note "${this.state.creditNoteData.custom_id || this.state.creditNoteData.id}" voided`,
                path: successUrl.split("?")[0],
                barePathOnly: true,
                delayRender: true,
            })
            history.replaceState({}, "", "")
            location.assign(successUrl)
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Credit Note could not be voided",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, undefined, undefined)
    }

    // CRUD Contact

    createContact = async () => {
        const populationRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].populationRef

        const contactData = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]]

        const { isValid, errors } = validateSendContact(
            contactData,
            [populationRef === "updateSendPhoneSelection" ? "phone" : "email"]
        )

        if (isValid) {
            const endpoint = DjangoUrls["clients:api-clients-contacts-list"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.external_client.id)
            const endpointMethod = "POST"

            const onSuccess = (contact) => {
                let destinationMode = null
                let destinationData = null

                if (populationRef === "updateSendPhoneSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)

                    if (valueIsDefined(contact.phone)) {
                        const newContacts = [...this.state.selectedSendPhoneContacts, contact]
                        destinationData.phone_contacts = newContacts
                        this[populationRef](newContacts)
                    }
                }
                else if (populationRef === "updateSendEmailSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)

                    if (valueIsDefined(contact.email)) {
                        const newContacts = [...this.state.selectedSendEmailContacts, contact]
                        destinationData.email_contacts = newContacts
                        this[populationRef](newContacts)
                    }
                }

                this.switchToPrimaryForm()
                this.switchToSecondaryForm(destinationMode, destinationData, null)
                this.addToastToQueue({
                    type: "success",
                    variant: contact.is_ephemeral ? "detailed" : "default",
                    title: `Contact ${contact.is_ephemeral ? "added" : "created"}`,
                    supportText: `This contact will not be saved to the contact list`
                })
            }
            const onError = () => {
                this.addToastToQueue({
                    type: "error",
                    variant: "default",
                    title: `Contact could not be ${contactData.is_ephemeral ? "added" : "created"}`,
                })
            }

            this.createUpdateContact(endpoint, endpointMethod, onSuccess, onError)
        }
        else {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: `Contact could not be ${contactData.is_ephemeral ? "added" : "created"}`,
            })
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = errors
                return updatedState
            })
        }
    }

    updateContact = async () => {
        const populationRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].populationRef
        const dataRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].dataRef

        const { isValid, errors } = validateSendContact(
            this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]],
            [populationRef === "updateSendPhoneSelection" ? "phone" : "email"]
        )

        if (isValid) {
            const endpoint = DjangoUrls["clients:api-clients-contacts-detail"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.external_client.id, this.state[dataRef].id)
            const endpointMethod = "PUT"

            const onSuccess = (contact) => {
                let destinationMode = null
                let destinationData = null

                if (populationRef === "updateSendPhoneSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)

                    if (valueIsDefined(contact.phone)) {
                        const newContacts = [...this.state.selectedSendPhoneContacts, contact]
                        destinationData.phone_contacts = newContacts
                        this[populationRef](newContacts)
                    }
                }
                else if (populationRef === "updateSendEmailSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)

                    if (valueIsDefined(contact.email)) {
                        const newContacts = [...this.state.selectedSendEmailContacts, contact]
                        destinationData.email_contacts = newContacts
                        this[populationRef](newContacts)
                    }
                }

                this.switchToPrimaryForm()
                this.switchToSecondaryForm(destinationMode, destinationData, null)
                this.addToastToQueue({
                    type: "success",
                    variant: "default",
                    title: "Contact updated",
                })
            }
            const onError = () => {
                this.addToastToQueue({
                    type: "error",
                    variant: "default",
                    title: "Contact could not be updated",
                })
            }

            this.createUpdateContact(endpoint, endpointMethod, onSuccess, onError)
        }
        else {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "Contact could not be updated",
            })
            this.setState((state, props) => {
                let updatedState = state
                updatedState.errors[ERROR_NAMES_BY_MODE[state.mode]] = errors
                return updatedState
            })
        }
    }

    createUpdateContact = async (endpoint, endpointMethod, onSuccess, onError) => {
        const dataName = "contactData"
        const submittingName = "submittingContact"
        const errorDictName = "contact"

        const setErrors = (fieldName, message, errorDict) => {
            if (fieldName === "non_field_errors" && message === "A contact with these details already exists.") {
                errorDict["non_field_error"] = "A contact with these details already exists."
            }
            else {
                errorDict[fieldName] = message
            }
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, undefined, setErrors)
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "CREDIT_NOTE_CREATE":
                this.createCreditNote()
                break
            case "CREDIT_NOTE_EDIT":
                location.assign(DjangoUrls["invoices:credit-notes-update"](window.MARKETPLACE_ENTITY_SLUG, this.state.creditNoteData.id))
                break
            case "CREDIT_NOTE_SEND":
                this.switchToSecondaryForm(PAGE_MODES.SEND_CREDIT_NOTE, null, null)
                break
            case "TOGGLE_SEND_VIA_SMS":
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.sendViaSMS = !state.sendViaSMS
                    return updatedState
                })
                break
            case "TOGGLE_SEND_VIA_EMAIL":
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.sendViaEmail = !state.sendViaEmail
                    return updatedState
                })
                break
            case "TOGGLE_SEND_MANUALLY":
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.sendManually = !state.sendManually
                    return updatedState
                })
                break
            case "CREDIT_NOTE_SEND_SUBMIT":
                if (!(this.state.sendViaSMS || this.state.sendViaEmail || this.state.sendManually)) {
                    // Check that at least one method is selected
                    this.setState((state, props) => {
                        let updatedState = state
                        updatedState.errors.send.deliveryMethod = "Please select at least one delivery method."
                        return updatedState
                    })
                }
                else {
                    if (this.state.creditNoteData.state_label === "Issued" || this.state.sendViaSMS || this.state.sendViaEmail) {
                        this.submitSendData()
                    }
                    else {
                        // Mark as sent if it's not yet sent or if they choose not to send it via SMS or Email
                        window.markCreditNoteAsSent = this.markCreditNoteAsSent
                        document.querySelector("#message_modal_mark_as_sent").style.display = ""
                        window.MicroModal.show("message_modal_mark_as_sent")
                    }
                }
                break
            case "CREDIT_NOTE_VOID":
                this.switchToSecondaryForm(PAGE_MODES.VOID_CREDIT_NOTE, null, null)
                break
            case "CREDIT_NOTE_VOID_SUBMIT":
                this.voidCreditNote()
                break
            case "CREDIT_NOTE_DOWNLOAD_PDF":
                location.assign(window.PUBLIC_PDF_URL)
                break
            case "CONTACT_CREATE":
                this.createContact()
                break
            case "CONTACT_UPDATE":
                const dataRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].dataRef

                if (this.state[dataRef].is_ephemeral) {
                    this.createContact()
                }
                else {
                    this.updateContact()
                }
                break
            case "CONTACT_CANCEL_CREATE":
            case "CONTACT_CANCEL_EDITS":
                const populationRef = this.state[FORM_DATA_NAMES_BY_MODE[this.state.mode]].populationRef

                let destinationMode = null
                let destinationData = null

                if (populationRef === "updateSendPhoneSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)
                }
                else if (populationRef === "updateSendEmailSelection") {
                    destinationMode = PAGE_MODES.SEND_CREDIT_NOTE
                    destinationData = deepcopy()(this.state.sendData)
                }

                this.switchToPrimaryForm()
                this.switchToSecondaryForm(destinationMode, destinationData, null)
                break
            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render() {
        if (this.state.creditNoteData === null) {
            return <Spinner centered={true} />
        }
        else {
            if (PRIMARY_PAGE_MODES.includes(this.state.mode)) {
                return <CreditNoteDetailsCard
                    creditNote={this.state.creditNoteData}
                    requestAction={this.handleActionRequest}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    returnScroll={this.state.returnScroll}
                    submitting={this.state.submittingCreditNote}
                    errors={this.state.errors.creditNote}
                    addToastToQueue={this.addToastToQueue}
                ></CreditNoteDetailsCard>
            }
            else if (this.state.mode === PAGE_MODES.SEND_CREDIT_NOTE) {
                return <CreditNoteSendForm
                    creditNote={this.state.creditNoteData}
                    sendData={this.state.sendData}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    switchToSecondaryForm={this.switchToSecondaryForm}
                    submitting={this.state.submittingSend}
                    errors={this.state.errors.send}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("sendData", fieldName, fieldValue)}
                    updateSendPhoneSelection={this.updateSendPhoneSelection}
                    updateSendEmailSelection={this.updateSendEmailSelection}
                    selectedSendPhoneContacts={this.state.selectedSendPhoneContacts}
                    selectedSendEmailContacts={this.state.selectedSendEmailContacts}
                    sendViaSMS={this.state.sendViaSMS}
                    sendViaEmail={this.state.sendViaEmail}
                    sendManually={this.state.sendManually}
                    returnScroll={0}
                ></CreditNoteSendForm>
            }
            else if (this.state.mode === PAGE_MODES.VOID_CREDIT_NOTE) {
                return <CreditNoteVoidForm
                    creditNote={this.state.creditNoteData}
                    voidData={this.state.voidData}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    submitting={this.state.submittingVoid}
                    errors={this.state.errors.void}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("voidData", fieldName, fieldValue)}
                    inventoryReturned={this.state.inventoryReturned}
                    returnScroll={0}
                ></CreditNoteVoidForm>
            }
            else if (CONTACT_PAGE_MODES.includes(this.state.mode)) {
                return <ContactForm
                    mode={this.state.mode}
                    submitting={this.state.submittingContact}
                    contact={this.state.contactData}
                    errors={this.state.errors.contact}
                    onFormDataChange={(fieldName, fieldValue) => this.updateFormData("contactData", fieldName, fieldValue)}
                    requestAction={this.handleActionRequest}
                    switchToPrimaryForm={this.switchToPrimaryForm}
                    returnScroll={0}
                ></ContactForm>
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
    }
}

export default CreditNoteDetailsContainer;
